import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Button, Container, Table, Spinner, Modal, Form, Alert, Row, Col, Card, InputGroup, Badge, Collapse } from 'react-bootstrap';
import { getSettlements, getSuppliers, getDeductibleExpenses, createSettlement, updateSettlement, deleteSettlement } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Select from 'react-select';
import Pagination from '../components/Pagination';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import PageSizeSelector from '../components/PageSizeSelector';
import { FaPrint, FaShareAlt, FaEdit, FaTrash, FaMoneyBillWave } from 'react-icons/fa';
import { BsWhatsapp, BsClipboardCheck, BsBoxArrowUpRight, BsCheckCircleFill, BsShareFill } from 'react-icons/bs';
import ShareModal from '../components/ShareModal';
import CurrencyInput from '../components/CurrencyInput';
import { formatExpenseCategory } from '../utils/expenseCategories';
import useIsMobile from '../hooks/useIsMobile';

const SupplierSettlementsPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [settlements, setSettlements] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [deductibleExpenses, setDeductibleExpenses] = useState([]);

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);

    const [loading, setLoading] = useState(true);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Share Modal State
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareableLink, setShareableLink] = useState('');
    const [settlementToShare, setSettlementToShare] = useState(null);

    const getLocalISODate = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = getLocalISODate();

    // Get first day of current month
    const getFirstDayOfMonth = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const initialModalState = {
        supplier_id: '',
        period_start: getFirstDayOfMonth(),
        period_end: today,
        gross_amount: '',
        expense_ids: [],
        notes: ''
    };

    const [modalData, setModalData] = useState(initialModalState);
    const [editingSettlement, setEditingSettlement] = useState(null);

    // Pay modal state
    const [payModalData, setPayModalData] = useState({
        payment_method: 'Transfer',
        payment_date: today,
        notes: ''
    });
    const [settlementToPay, setSettlementToPay] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalSettlementsCount, setTotalSettlementsCount] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        supplier_id: '',
        status: ''
    });
    const [activeFilters, setActiveFilters] = useState({
        supplier_id: '',
        status: ''
    });

    const fetchSettlements = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: pageSize,
                supplier_id: activeFilters.supplier_id || undefined,
                status: activeFilters.status || undefined
            };
            const response = await getSettlements(params);
            setSettlements(response.data.settlements);
            setTotalPages(response.data.totalPages);
            setTotalSettlementsCount(response.data.totalSettlements);
            setError('');
        } catch (error) {
            console.error(error);
            toast.error(t('settlementsPage.toasts.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, activeFilters, t]);

    const fetchSuppliers = async () => {
        try {
            const response = await getSuppliers({ pagination: false });
            setSuppliers(response.data);
        } catch (error) {
            console.error("Failed to load suppliers", error);
        }
    };

    const fetchDeductibleExpenses = async (supplierId) => {
        if (!supplierId) {
            setDeductibleExpenses([]);
            return;
        }
        try {
            setLoadingExpenses(true);
            const response = await getDeductibleExpenses(supplierId);
            setDeductibleExpenses(response.data);
        } catch (error) {
            console.error("Failed to load deductible expenses", error);
            setDeductibleExpenses([]);
        } finally {
            setLoadingExpenses(false);
        }
    };

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_EXPENSES)) {
            fetchSettlements();
            fetchSuppliers();
        } else {
            setLoading(false);
            setError(t('settlementsPage.toasts.permissionError'));
        }
    }, [fetchSettlements, hasPermission, t]);

    const supplierOptions = useMemo(() => suppliers.map(s => ({
        value: s.supplier_id,
        label: s.name
    })), [suppliers]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const applyFilters = () => {
        setCurrentPage(1);
        setActiveFilters(filters);
    };

    const clearFilters = () => {
        const initialFilters = { supplier_id: '', status: '' };
        setFilters(initialFilters);
        setActiveFilters(initialFilters);
        setCurrentPage(1);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingSettlement(null);
        setModalData(initialModalState);
        setDeductibleExpenses([]);
    };

    const handleCreateClick = () => {
        setEditingSettlement(null);
        setModalData(initialModalState);
        setShowModal(true);
    };

    const handleDelete = async (settlementId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('settlementsPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('settlementsPage.actions.delete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            const toastId = toast.loading(t('settlementsPage.toasts.deleting'));
            try {
                await deleteSettlement(settlementId);
                fetchSettlements();
                trackAction('settlement_deleted');
                toast.success(t('settlementsPage.toasts.deleted'), { id: toastId });
            } catch (error) {
                toast.error(t('settlementsPage.toasts.deleteFailed'), { id: toastId });
            }
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        const toastId = toast.loading(t('settlementsPage.toasts.creating'));
        try {
            await createSettlement(modalData);
            trackAction('settlement_created');
            toast.success(t('settlementsPage.toasts.created'), { id: toastId });
            fetchSettlements();
            handleModalClose();
        } catch (error) {
            trackAction('settlement_save_failed');
            toast.error(t('settlementsPage.toasts.createFailed'), { id: toastId });
        }
    };

    const handleSupplierChange = (selectedOption) => {
        const supplierId = selectedOption ? selectedOption.value : '';
        setModalData({ ...modalData, supplier_id: supplierId, expense_ids: [] });
        fetchDeductibleExpenses(supplierId);
    };

    const handleExpenseToggle = (expenseId) => {
        setModalData(prev => ({
            ...prev,
            expense_ids: prev.expense_ids.includes(expenseId)
                ? prev.expense_ids.filter(id => id !== expenseId)
                : [...prev.expense_ids, expenseId]
        }));
    };

    const handleSelectAllExpenses = () => {
        if (modalData.expense_ids.length === deductibleExpenses.length) {
            setModalData({ ...modalData, expense_ids: [] });
        } else {
            setModalData({ ...modalData, expense_ids: deductibleExpenses.map(e => e.expense_id) });
        }
    };

    // Calculate totals
    const totalDeductions = useMemo(() => {
        return deductibleExpenses
            .filter(e => modalData.expense_ids.includes(e.expense_id))
            .reduce((sum, e) => sum + parseFloat(e.amount), 0);
    }, [deductibleExpenses, modalData.expense_ids]);

    const netAmount = useMemo(() => {
        const gross = parseFloat(modalData.gross_amount) || 0;
        return gross - totalDeductions;
    }, [modalData.gross_amount, totalDeductions]);

    const handlePayClick = (settlement) => {
        setSettlementToPay(settlement);
        setPayModalData({
            payment_method: 'Transfer',
            payment_date: today,
            notes: settlement.notes || ''
        });
        setShowPayModal(true);
    };

    const handlePaySubmit = async (e) => {
        e.preventDefault();
        const toastId = toast.loading(t('settlementsPage.toasts.paying'));
        try {
            await updateSettlement(settlementToPay.settlement_id, {
                status: 'PAID',
                payment_date: payModalData.payment_date,
                payment_method: payModalData.payment_method,
                notes: payModalData.notes
            });
            trackAction('settlement_paid', { payment_method: payModalData.payment_method });
            toast.success(t('settlementsPage.toasts.paid'), { id: toastId });
            fetchSettlements();
            setShowPayModal(false);
        } catch (error) {
            trackAction('settlement_pay_failed');
            toast.error(t('settlementsPage.toasts.payFailed'), { id: toastId });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handlePrintClick = (settlement) => {
        const link = `${window.location.origin}/settlement-receipt/${settlement.public_id}?print=true`;
        window.open(link, '_blank');
    };

    const handleShareClick = (settlement) => {
        const link = `${window.location.origin}/settlement-receipt/${settlement.public_id}`;
        setShareableLink(link);
        setSettlementToShare(settlement);
        setShowShareModal(true);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareableLink);
        toast.success(t('toast.linkCopied'));
    };
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(amount || 0);
    };

    // Check for feature availability
    if (!business?.plan_features?.supplier_settlements) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    <Alert.Heading>{t('common.featureNotAvailable')}</Alert.Heading>
                    <p>
                        {t('settlementsPage.featureRequiredMessage', 'Esta funcionalidad de Liquidaciones a Proveedores no está incluida en tu plan actual. Por favor contacta a soporte o actualiza tu plan para acceder a ella.')}
                    </p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-5">
            <Row className="align-items-center mb-4">
                <Col md={8}>
                    <h2>{t('settlementsPage.title')}</h2>
                    <p className="text-muted mb-0">{t('settlementsPage.subtitle')}</p>
                </Col>
                <Col md={4} className="text-end">
                    {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                        <div title={!isSubscriptionActive ? t('subscriptionRequired') : ''} className="d-none d-md-inline-block">
                            <Button
                                variant="primary"
                                onClick={handleCreateClick}
                                disabled={!isSubscriptionActive}
                            >
                                {t('settlementsPage.newSettlementButton')}
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>

            {/* Mobile Fixed Bottom Button */}
            {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                <div className="d-md-none position-fixed bottom-0 start-0 end-0 px-3 pt-3 bg-white shadow-lg" style={{ zIndex: 1000, borderTop: '1px solid #dee2e6', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                    <Button
                        variant="primary"
                        onClick={handleCreateClick}
                        disabled={!isSubscriptionActive}
                        className="w-100"
                    >
                        {t('settlementsPage.newSettlementButton')}
                    </Button>
                </div>
            )}

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('settlementsPage.hideFilters', 'Ocultar filtros') : t('settlementsPage.viewFilters', 'Ver filtros')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="settlements-filters">
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body>
                            <Form>
                                <Row className="align-items-end">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t('settlementsPage.filter.supplier')}</Form.Label>
                                            <Form.Select name="supplier_id" value={filters.supplier_id} onChange={handleFilterChange}>
                                                <option value="">{t('settlementsPage.filter.allSuppliers')}</option>
                                                {suppliers.map(s => (
                                                    <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t('settlementsPage.filter.status')}</Form.Label>
                                            <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                                                <option value="">{t('settlementsPage.filter.allStatuses')}</option>
                                                <option value="PENDING">{t('settlementsPage.status.PENDING')}</option>
                                                <option value="PAID">{t('settlementsPage.status.PAID')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} className="d-flex">
                                        <Button variant="primary" onClick={applyFilters} className="me-2 flex-grow-1">
                                            {t('settlementsPage.filter.apply')}
                                        </Button>
                                        <Button variant="secondary" onClick={clearFilters} className="flex-grow-1">
                                            {t('settlementsPage.filter.clear')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    <div className="modern-table-wrapper mt-3">
                        <Table hover responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>{t('settlementsPage.table.period')}</th>
                                    <th>{t('settlementsPage.table.supplier')}</th>
                                    <th className="text-end">{t('settlementsPage.table.grossAmount')}</th>
                                    <th className="text-end">{t('settlementsPage.table.deductions')}</th>
                                    <th className="text-end">{t('settlementsPage.table.netAmount')}</th>
                                    <th className="text-center">{t('settlementsPage.table.status')}</th>
                                    <th className="text-center">{t('settlementsPage.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settlements.length > 0 ? settlements.map(settlement => (
                                    <tr key={settlement.settlement_id}>
                                        <td>
                                            {new Date(settlement.period_start).toLocaleDateString('es-CO', { timeZone: 'UTC' })} - {new Date(settlement.period_end).toLocaleDateString('es-CO', { timeZone: 'UTC' })}
                                        </td>
                                        <td>{settlement.supplier_name}</td>
                                        <td className="text-end">{formatCurrency(settlement.gross_amount)}</td>
                                        <td className="text-end text-danger">-{formatCurrency(settlement.total_deductions)}</td>
                                        <td className="text-end fw-bold">{formatCurrency(settlement.net_amount)}</td>
                                        <td className="text-center">
                                            {settlement.status === 'PAID' ? (
                                                <Badge bg="success"><BsCheckCircleFill className="me-1" />{t('settlementsPage.status.PAID')}</Badge>
                                            ) : (
                                                <Badge bg="warning" text="dark">{t('settlementsPage.status.PENDING')}</Badge>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="btn-actions">
                                                <Button variant="info" size="sm" onClick={() => handleShareClick(settlement)} title={t('settlementsPage.actions.share')}>
                                                    <FaShareAlt />
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={() => handlePrintClick(settlement)} title={t('settlementsPage.actions.print')}>
                                                    <FaPrint />
                                                </Button>
                                                {settlement.status === 'PENDING' && hasPermission(PERMISSIONS.EDIT_EXPENSES) && (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => handlePayClick(settlement)}
                                                        title={t('settlementsPage.actions.pay')}
                                                        aria-label={t('settlementsPage.actions.pay')}
                                                    >
                                                        <FaMoneyBillWave />
                                                    </Button>
                                                )}
                                                {hasPermission(PERMISSIONS.DELETE_EXPENSES) && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(settlement.settlement_id)}
                                                        title={t('settlementsPage.actions.delete')}
                                                        aria-label={t('settlementsPage.actions.delete')}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7" className="text-center py-4 text-muted">{t('settlementsPage.noSettlements')}</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                        <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
                    </div>
                </>
            )}

            {/* Create Settlement Modal */}
            <Modal show={showModal} onHide={handleModalClose} size="lg" backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('settlementsPage.modal.createTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleModalSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('settlementsPage.modal.supplierLabel')} *</Form.Label>
                                    <Select
                                        options={supplierOptions}
                                        onChange={handleSupplierChange}
                                        value={supplierOptions.find(opt => opt.value === modalData.supplier_id) || null}
                                        placeholder={t('settlementsPage.modal.supplierPlaceholder')}
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('settlementsPage.modal.periodStartLabel')} *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={modalData.period_start}
                                        onChange={(e) => setModalData({ ...modalData, period_start: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('settlementsPage.modal.periodEndLabel')} *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={modalData.period_end}
                                        onChange={(e) => setModalData({ ...modalData, period_end: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('settlementsPage.modal.grossAmountLabel')} *</Form.Label>
                                    <CurrencyInput
                                        value={modalData.gross_amount}
                                        onChange={(value) => setModalData({ ...modalData, gross_amount: value })}
                                        placeholder="0"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Deductible Expenses Section */}
                        {modalData.supplier_id && (
                            <div className="border rounded p-3 mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">{t('settlementsPage.modal.deductionsTitle')}</h6>
                                    {deductibleExpenses.length > 0 && (
                                        <Button variant="link" size="sm" onClick={handleSelectAllExpenses}>
                                            {modalData.expense_ids.length === deductibleExpenses.length
                                                ? t('settlementsPage.modal.deselectAll')
                                                : t('settlementsPage.modal.selectAll')}
                                        </Button>
                                    )}
                                </div>
                                {loadingExpenses ? (
                                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                                ) : deductibleExpenses.length > 0 ? (
                                    <div className="modern-table-wrapper">
                                        <Table className="modern-table modern-table-sm mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '30px' }}></th>
                                                    <th>{t('settlementsPage.modal.expenseDate')}</th>
                                                    <th>{t('settlementsPage.modal.expenseCategory')}</th>
                                                    <th>{t('settlementsPage.modal.expenseDescription')}</th>
                                                    <th className="text-end">{t('settlementsPage.modal.expenseAmount')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deductibleExpenses.map(expense => (
                                                    <tr key={expense.expense_id} className={modalData.expense_ids.includes(expense.expense_id) ? 'table-warning' : ''}>
                                                        <td>
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={modalData.expense_ids.includes(expense.expense_id)}
                                                                onChange={() => handleExpenseToggle(expense.expense_id)}
                                                            />
                                                        </td>
                                                        <td>{new Date(expense.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</td>
                                                        <td>{formatExpenseCategory(expense.category, t)}</td>
                                                        <td>{expense.description || '-'}</td>
                                                        <td className="text-end">{formatCurrency(expense.amount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <p className="text-muted mb-0">{t('settlementsPage.modal.noDeductibleExpenses')}</p>
                                )}
                            </div>
                        )}

                        {/* Summary */}
                        <Card className="bg-light">
                            <Card.Body>
                                <Row>
                                    <Col md={4} className="text-center">
                                        <small className="text-muted">{t('settlementsPage.modal.grossAmount')}</small>
                                        <h5>{formatCurrency(modalData.gross_amount || 0)}</h5>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <small className="text-muted">{t('settlementsPage.modal.totalDeductions')}</small>
                                        <h5 className="text-danger">-{formatCurrency(totalDeductions)}</h5>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <small className="text-muted">{t('settlementsPage.modal.netAmount')}</small>
                                        <h5 className={netAmount >= 0 ? 'text-success' : 'text-danger'}>{formatCurrency(netAmount)}</h5>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('settlementsPage.modal.notesLabel')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={modalData.notes}
                                onChange={(e) => setModalData({ ...modalData, notes: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleModalClose}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit" disabled={!modalData.supplier_id || !modalData.gross_amount}>
                            Guardar
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Pay Settlement Modal */}
            <Modal show={showPayModal} onHide={() => setShowPayModal(false)} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('settlementsPage.payModal.title')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePaySubmit}>
                    <Modal.Body>
                        {settlementToPay && (
                            <Alert variant="info">
                                <strong>{t('settlementsPage.payModal.supplier')}:</strong> {settlementToPay.supplier_name}<br />
                                <strong>{t('settlementsPage.payModal.amount')}:</strong> {formatCurrency(settlementToPay.net_amount)}
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('settlementsPage.payModal.paymentMethodLabel')}</Form.Label>
                            <Form.Select
                                value={payModalData.payment_method}
                                onChange={(e) => setPayModalData({ ...payModalData, payment_method: e.target.value })}
                            >
                                <option value="Cash">{t('expensesPage.paymentMethods.Cash')}</option>
                                <option value="Transfer">{t('expensesPage.paymentMethods.Transfer')}</option>
                                <option value="Card">{t('expensesPage.paymentMethods.Card')}</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('settlementsPage.payModal.paymentDateLabel')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={payModalData.payment_date}
                                onChange={(e) => setPayModalData({ ...payModalData, payment_date: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('settlementsPage.payModal.notesLabel')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={payModalData.notes}
                                onChange={(e) => setPayModalData({ ...payModalData, notes: e.target.value })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPayModal(false)}>{t('cancel')}</Button>
                        <Button variant="success" type="submit">
                            {t('settlementsPage.payModal.confirmButton')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Share Modal */}
            <ShareModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                shareableLink={shareableLink}
                message={settlementToShare ? `Liquidación de Proveedor - ${settlementToShare.supplier_name}.Período: ${new Date(settlementToShare.period_start).toLocaleDateString('es-CO', { timeZone: 'UTC' })} - ${new Date(settlementToShare.period_end).toLocaleDateString('es-CO', { timeZone: 'UTC' })}. Neto a pagar: ${formatCurrency(settlementToShare.net_amount)}. Ver aquí: ${shareableLink} ` : ''}
                title='Liquidación de Proveedor'
                phoneNumber={null}
                onPrint={() => handlePrintClick(settlementToShare)}
                openButtonLabel={t('settlementsPage.actions.open')}
            />
        </Container >
    );
};

export default SupplierSettlementsPage;
